import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { useWallet } from '../components/WalletContext';
import { web } from '@klever/sdk-web';
import { 
  FaListAlt, 
  FaSpinner, 
  FaImage,
  FaClipboardCheck,
  FaTimesCircle,
  FaExclamationTriangle
} from 'react-icons/fa';

import skyGalleryImage from '../assets/skygallery.png';
import klevernftImage from '../assets/klever.png';
import minthreeImage from '../assets/minthree.png';
import xportImage from '../assets/xport.png';
import RareCanvasImage from '../assets/logoicon2.png';
import world from '../assets/world.png';
import artGallery from '../assets/artgallery.png';
import '../styles/WalletListingsPage.css';

const marketplaceMapping = {
  '7c353c02770da029': 'Sky Gallery',
  'd4f2bab340c55fde': 'KleverNFT',
  '293693ee43db509': 'Minthree',
  'd2a04fe890a6edda': 'NFT Art Gallery',
  'aa68ad853a09988c': 'Xport',
  '417b70c0eb7a33cb': 'Rare Canvas',
  '81376f526cf47730': 'World dex'
};

const marketplaceImages = {
  'Sky Gallery': skyGalleryImage,
  'KleverNFT': klevernftImage,
  'Minthree': minthreeImage,
  'Xport': xportImage,
  'Rare Canvas': RareCanvasImage,
  'World dex': world,
  'NFT Art Gallery': artGallery,
};

const statusIcons = {
  'Active': 'fas fa-check-circle',
  'Cancelled': 'fas fa-times-circle',
  'Claimed': 'fas fa-clipboard-check',
  'Sold': 'fas fa-dollar-sign',
  'Expired': 'fas fa-hourglass-end'
};

// Utility: Create axios instance with timeout
const createAxiosWithTimeout = (timeout = 5000) => {
  return axios.create({
    timeout,
    headers: { 'Content-Type': 'application/json' }
  });
};

// Utility: Fetch with timeout and retry
const fetchWithTimeout = async (url, timeout = 5000, retries = 2) => {
  const axiosInstance = createAxiosWithTimeout(timeout);

  for (let i = 0; i < retries; i++) {
    try {
      const response = await axiosInstance.get(url);
      return response.data;
    } catch (error) {
      // On final retry, check if it's a timeout or network error
      if (i === retries - 1) {
        if (error.code === 'ECONNABORTED' || error.code === 'ERR_NETWORK') {
          // Network/timeout errors - fail silently for non-critical calls
          console.warn('Network timeout for:', url);
          return null;
        }
        throw error;
      }
      await new Promise(resolve => setTimeout(resolve, 1000 * (i + 1)));
    }
  }
};

const WalletListingsPage = () => {
  const { walletAddress, useKdaFee, selectedKda } = useWallet();
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [expiredOrderCount, setExpiredOrderCount] = useState(0);
  const [activeOrderCount, setActiveOrderCount] = useState(0);
  const [modalVisible, setModalVisible] = useState(false);
  const [modalAction, setModalAction] = useState('');
  const [modalCount, setModalCount] = useState(0);
  const [modalCost, setModalCost] = useState(0);
  const [ordersToClaim, setOrdersToClaim] = useState([]);
  const [transactionStatus, setTransactionStatus] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  // Fetch order status with timeout
  const fetchOrderStatus = async (orderId, endTime) => {
    try {
      const data = await fetchWithTimeout(
        `https://api.mainnet.klever.org/v1.0/transaction/list?limit=20&page=1&orderid=${orderId}`,
        5000
      );

      // Handle null response from timeout
      if (!data || !data.data || !data.data.transactions) {
        return 'Active'; // Default on network failure
      }

      const txs = data.data.transactions;
      let status = 'Active';
      const currentTime = Math.floor(Date.now() / 1000);
      
      txs.sort((a, b) => a.blockNum - b.blockNum);
      
      for (const tx of txs) {
        if (tx.contract.some(c => c.typeString === "CancelMarketOrderContractType" && c.parameter.orderID === orderId)) {
          status = 'Cancelled';
          break;
        } else if (tx.contract.some(c => c.typeString === "ClaimContractType" && c.parameter.id === orderId)) {
          status = 'Claimed';
          break;
        } else if (tx.contract.some(c => c.typeString === "BuyContractType" && c.parameter.id === orderId)) {
          status = 'Sold';
          break;
        }
      }
      
      if (currentTime > endTime && status === 'Active') {
        status = 'Expired';
      }
      
      return status;
    } catch (error) {
      console.error('Error fetching order status for order ID:', orderId, error);
      return 'Active'; // Default to Active on error
    }
  };

  // Fetch metadata with comprehensive error handling
  const fetchMetadata = async (collectionId, nftId) => {
    try {
      // Fetch asset list with timeout
      const assetData = await fetchWithTimeout(
        'https://api.mainnet.klever.org/v1.0/assets/list?limit=100',
        5000
      );

      // Handle null response from timeout
      if (!assetData || !assetData.data || !assetData.data.assets) {
        return null;
      }

      const assets = assetData.data.assets;
      const matchingAsset = assets.find(a => a.assetId === collectionId);
      
      if (!matchingAsset) {
        console.warn('Asset not found for collection ID:', collectionId);
        return null;
      }

      const metadataObject = matchingAsset.uris?.find(uri => uri.key.toLowerCase() === 'metadata');
      let metadataUri = metadataObject ? metadataObject.value : null;

      if (!metadataUri) {
        console.warn('No metadata URI found for:', nftId);
        return null;
      }

      // Handle special cases
      if (metadataUri === "QmNaa2KQ6NkjjESpPHEnAow9hivnsAkq2Gd6R26cHG28Er") {
        metadataUri = `https://ipfs.io/ipfs/${metadataUri}/${nftId}.json`;
      } else {
        if (metadataUri.includes("klever-mint.mypinata.cloud")) {
          metadataUri = metadataUri.replace("https://klever-mint.mypinata.cloud", "https://ipfs.io");
        }
        metadataUri = `${metadataUri}/${nftId}`;
      }

      // Try primary fetch with timeout
      try {
        const metadata = await fetchWithTimeout(metadataUri, 5000, 1);
        
        // Fix image URLs
        if (metadata.image) {
          if (metadata.image.includes("www.lovemonsternft.com")) {
            const imageName = metadata.image.split('/').pop();
            metadata.image = `https://ipfs.io/ipfs/QmWVmUDPBeQzv6fG93JxQxFVee8b6smFD3RQosQXJHiZTJ/${imageName}`;
          } else if (metadata.image.includes("klever-mint.mypinata.cloud")) {
            metadata.image = metadata.image.replace("https://klever-mint.mypinata.cloud", "https://ipfs.io");
          }
        }
        
        return metadata;
      } catch (primaryError) {
        // Try fallback
        console.warn('Primary fetch failed, trying fallback for', nftId);
        try {
          const fallbackUri = `https://${metadataUri}.ipfs.w3s.link/${nftId}`;
          const fallbackData = await fetchWithTimeout(fallbackUri, 5000, 1);
          return fallbackData;
        } catch (fallbackError) {
          console.error('Fallback also failed for', nftId);
          return null;
        }
      }
    } catch (error) {
      console.error(`Error fetching metadata for ${collectionId}/${nftId}:`, error);
      return null;
    }
  };

  // Fetch transactions with proper error handling
  const fetchTransactions = async (page) => {
    setLoading(true);
    setErrorMessage('');

    try {
      const data = await fetchWithTimeout(
        `https://api.mainnet.klever.org/v1.0/transaction/list?limit=20&page=${page}&type=18&status=success&fromAddress=${walletAddress}`,
        15000, // Increased timeout for main fetch
        3 // Extra retry for main fetch
      );

      // Check for null response
      if (!data || !data.data || !data.data.transactions) {
        setErrorMessage('Unable to connect to blockchain API. Please try again later.');
        setTransactions([]);
        setLoading(false);
        return;
      }

      // Use Promise.allSettled to handle failures gracefully
      const contractPromises = data.data.transactions.flatMap((tx) => {
        const sellContracts = tx.contract.filter(c => c.typeString === "SellContractType");
        
        return sellContracts.map(async (sellContract, index) => {
          try {
            const [collectionId, nftNonce] = sellContract.parameter.assetId.split('/');
            const price = sellContract.parameter.price / 1000000;
            const marketplaceName = marketplaceMapping[sellContract.parameter.marketplaceID] || 'Unknown Marketplace';
            
            const sellReceipt = tx.receipts.find(r => r.typeString === "Sell" && r.cID === index);
            const orderId = sellReceipt ? sellReceipt.orderId : 'No Order ID';
            
            // Fetch status and metadata with error handling
            const [status, metadata] = await Promise.allSettled([
              fetchOrderStatus(orderId, sellContract.parameter.endTime),
              fetchMetadata(collectionId, nftNonce)
            ]);

            const finalStatus = status.status === 'fulfilled' ? status.value : 'Active';
            const imageUrl = metadata.status === 'fulfilled' && metadata.value ? metadata.value.image : null;

            return {
              ...tx,
              collectionId,
              nftNonce,
              price,
              marketplaceName,
              imageUrl,
              orderId,
              status: finalStatus,
              hasError: false
            };
          } catch (error) {
            console.error('Error processing contract:', error);
            // Return partial data even on error
            const [collectionId, nftNonce] = sellContract.parameter.assetId.split('/');
            return {
              ...tx,
              collectionId,
              nftNonce,
              price: sellContract.parameter.price / 1000000,
              marketplaceName: 'Unknown',
              imageUrl: null,
              orderId: 'Error',
              status: 'Active',
              hasError: true
            };
          }
        });
      });

      const results = await Promise.allSettled(contractPromises);
      const validTxs = results
        .filter(result => result.status === 'fulfilled' && result.value !== null)
        .map(result => result.value);

      setTransactions(validTxs);
      setTotalPages(data.pagination.totalPages);
      setExpiredOrderCount(validTxs.filter(tx => tx.status === 'Expired').length);
      setActiveOrderCount(validTxs.filter(tx => tx.status === 'Active').length);
    } catch (error) {
      console.error('Error fetching transactions:', error);

      // Provide more specific error messages
      if (error.code === 'ECONNABORTED') {
        setErrorMessage('Request timed out. The blockchain API may be slow. Please try again.');
      } else if (error.code === 'ERR_NETWORK') {
        setErrorMessage('Network error. Please check your connection and try again.');
      } else if (error.response?.status === 429) {
        setErrorMessage('Too many requests. Please wait a moment and try again.');
      } else {
        setErrorMessage('Failed to load listings. Please try again later.');
      }

      setTransactions([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (walletAddress) {
      fetchTransactions(currentPage);
    }
  }, [currentPage, walletAddress]);

  const handlePrevPage = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1);
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) setCurrentPage(currentPage + 1);
  };

  const formatDate = (timestamp) => {
    return new Date(timestamp * 1000).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const openModal = (action, count, cost, orders = []) => {
    setModalAction(action);
    setModalCount(count);
    setModalCost(cost);
    setOrdersToClaim(orders);
    setModalVisible(true);
    setTransactionStatus('');
  };

  const handleClaimAll = () => {
    const expiredOrders = transactions
      .filter(tx => tx.status === 'Expired')
      .slice(0, 20)
      .map(tx => tx.orderId)
      .filter(id => id !== 'No Order ID' && id !== 'Error');
    
    if (expiredOrders.length === 0) {
      setErrorMessage('No expired orders to claim.');
      return;
    }
    
    const cost = expiredOrders.length * 3;
    openModal('claim', expiredOrders.length, cost, expiredOrders);
  };

  const handleDelistAll = () => {
    const activeOrders = transactions
      .filter(tx => tx.status === 'Active')
      .slice(0, 20)
      .map(tx => tx.orderId)
      .filter(id => id !== 'No Order ID' && id !== 'Error');
    
    if (activeOrders.length === 0) {
      setErrorMessage('No active orders to delist.');
      return;
    }
    
    const cost = activeOrders.length * 52;
    openModal('delist', activeOrders.length, cost, activeOrders);
  };

  const handleModalConfirm = async () => {
    if (modalAction === 'claim') {
      await claimAllNfts(ordersToClaim);
    } else if (modalAction === 'delist') {
      await delistAllOrders(ordersToClaim);
    }
  };

  const claimAllNfts = useCallback(async (orderIds) => {
    setTransactionStatus('processing');
    
    const contracts = orderIds.map(orderId => ({
      type: 9,
      payload: { claimType: 2, id: orderId }
    }));
    
    const txOptions = useKdaFee && selectedKda ? { kdaFee: selectedKda } : undefined;

    try {
      const unsignedTx = await web.buildTransaction(contracts, undefined, txOptions);
      const signedTx = await web.signTransaction(unsignedTx);
      
      if (!signedTx) {
        setTransactionStatus('error');
        return;
      }
      
      await web.broadcastTransactions([signedTx]);
      setTransactionStatus('success');
      
      setTimeout(() => {
        setModalVisible(false);
        fetchTransactions(currentPage);
      }, 2000);
    } catch (error) {
      console.error('Error in claim NFT transaction:', error);
      setTransactionStatus('error');
    }
  }, [useKdaFee, selectedKda, currentPage]);

  const delistAllOrders = useCallback(async (orderIds) => {
    setTransactionStatus('processing');
    
    const contracts = orderIds.map(orderId => ({
      type: 19,
      payload: { orderId: orderId }
    }));
    
    const txOptions = useKdaFee && selectedKda ? { kdaFee: selectedKda } : undefined;

    try {
      const unsignedTx = await web.buildTransaction(contracts, undefined, txOptions);
      const signedTx = await web.signTransaction(unsignedTx);
      
      if (!signedTx) {
        setTransactionStatus('error');
        return;
      }
      
      await web.broadcastTransactions([signedTx]);
      setTransactionStatus('success');
      
      setTimeout(() => {
        setModalVisible(false);
        fetchTransactions(currentPage);
      }, 2000);
    } catch (error) {
      console.error('Error in cancel order transaction:', error);
      setTransactionStatus('error');
    }
  }, [useKdaFee, selectedKda, currentPage]);

  if (!walletAddress) {
    return (
      <div className="wallet-listings-page">
        <div className="listings-loading">
          <FaExclamationTriangle className="listings-loading-spinner" style={{ fontSize: '3rem', color: 'var(--warning)' }} />
          <p className="listings-loading-text">Please connect your wallet to view listings.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="wallet-listings-page">
      {/* Hero Section */}
      <div className="listings-hero">
        <h1 className="listings-hero-title">
          <FaListAlt className="listings-hero-icon" />
          Your Listings
        </h1>
        <p className="listings-hero-subtitle">
          Manage your active and expired NFT listings on KleverChain
        </p>
      </div>

      {loading ? (
        <div className="listings-loading">
          <FaSpinner className="listings-loading-spinner" />
          <p className="listings-loading-text">Loading your listings...</p>
        </div>
      ) : (
        <>
          {/* Error Message */}
          {errorMessage && (
            <div className="listings-error">
              <FaExclamationTriangle />
              <span>{errorMessage}</span>
            </div>
          )}

          {/* Bulk Action Buttons */}
          {(expiredOrderCount > 0 || activeOrderCount > 0) && (
            <div className="wallet-button-group">
              {expiredOrderCount > 0 && (
                <button onClick={handleClaimAll} className="wallet-tab-button active">
                  <FaClipboardCheck />
                  <span>Claim All ({expiredOrderCount})</span>
                </button>
              )}
              {activeOrderCount > 0 && (
                <button onClick={handleDelistAll} className="wallet-tab-button active">
                  <FaTimesCircle />
                  <span>Delist All ({activeOrderCount})</span>
                </button>
              )}
            </div>
          )}

          {/* Listings Container */}
          <div className="listings-container">
            {transactions.length === 0 ? (
              <div className="listings-loading">
                <FaListAlt className="listings-loading-spinner" style={{ fontSize: '3rem', opacity: 0.5 }} />
                <p className="listings-loading-text">No listings found.</p>
              </div>
            ) : (
              transactions.map((tx, index) => (
                <Link 
                  to={`/collections/${tx.collectionId}/${tx.nftNonce}`} 
                  key={`${tx.orderId}-${index}`} 
                  className="activity-item-link"
                >
                  <div 
                    className="activity-item"
                    style={{ animationDelay: `${index * 0.05}s` }}
                  >
                    {/* NFT Image */}
                    {tx.imageUrl ? (
                      <img
                        src={tx.imageUrl}
                        alt={`NFT ${tx.collectionId}/${tx.nftNonce}`}
                        className="listings-nft-image"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                    ) : null}
                    <div 
                      className="listings-nft-image-placeholder"
                      style={{ display: tx.imageUrl ? 'none' : 'flex' }}
                    >
                      <FaImage />
                    </div>

                    {/* Details */}
                    <div className="activity-details">
                      <p>
                        <span className="date">{formatDate(tx.timestamp)}</span> — You listed{' '}
                        <span className="asset-id">{tx.collectionId}/{tx.nftNonce}</span> for{' '}
                        <span className="listed-price">{tx.price.toLocaleString()} KLV</span>
                      </p>
                      
                      <div className="marketplace-info">
                        <span className="marketplace">{tx.marketplaceName}</span>
                        {marketplaceImages[tx.marketplaceName] && (
                          <img
                            src={marketplaceImages[tx.marketplaceName]}
                            alt={tx.marketplaceName}
                            className="marketplace-image"
                          />
                        )}
                      </div>

                      <span className={`order-status status-${tx.status.toLowerCase()}`}>
                        <i className={statusIcons[tx.status]}></i>
                        <span>{tx.status}</span>
                      </span>
                    </div>
                  </div>
                </Link>
              ))
            )}
          </div>

          {/* Pagination */}
          {transactions.length > 0 && (
            <div className="pagination">
              <button onClick={handlePrevPage} disabled={currentPage <= 1}>
                <span>Previous</span>
              </button>
              <span>Page {currentPage} of {totalPages}</span>
              <button onClick={handleNextPage} disabled={currentPage >= totalPages}>
                <span>Next</span>
              </button>
            </div>
          )}
        </>
      )}

      {/* Modal */}
      {modalVisible && (
        <DelistingConfirmationModal
          action={modalAction}
          count={modalCount}
          cost={modalCost}
          transactionStatus={transactionStatus}
          onCancel={() => setModalVisible(false)}
          onConfirm={handleModalConfirm}
        />
      )}
    </div>
  );
};

const DelistingConfirmationModal = ({ action, count, cost, transactionStatus, onCancel, onConfirm }) => (
  <div className="modal-overlay" onClick={onCancel}>
    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
      <h2>Confirm {action === 'claim' ? 'Claim' : 'Delist'}</h2>
      <p>
        {action === 'claim' ? 'Claim' : 'Delist'} <strong>{count}</strong> NFT{count > 1 ? 's' : ''}
      </p>
      <p>
        Estimated cost: <span className="cost-highlight">{cost.toFixed(2)} KLV</span>
      </p>
      
      {transactionStatus && (
        <div className={`transaction-status ${transactionStatus}`}>
          {transactionStatus === 'processing' && (
            <>
              <FaSpinner style={{ animation: 'spin 1s linear infinite' }} />
              <span> Processing transaction...</span>
            </>
          )}
          {transactionStatus === 'success' && (
            <span>✓ Transaction completed successfully!</span>
          )}
          {transactionStatus === 'error' && (
            <span>✗ Transaction failed. Please try again.</span>
          )}
        </div>
      )}

      <div className="modal-buttons">
        <button onClick={onCancel} className="cancel-btn" disabled={transactionStatus === 'processing'}>
          Cancel
        </button>
        <button 
          onClick={onConfirm} 
          className="confirm-btn"
          disabled={transactionStatus === 'processing'}
        >
          {transactionStatus === 'processing' ? 'Processing...' : 'Confirm'}
        </button>
      </div>
    </div>
  </div>
);

export default WalletListingsPage;